import { useParams } from 'react-router-dom';
import { useToastContext } from '@/contexts/toastContext';
import useApiFetch from '@/hooks/useApiFetch';
import useApiSubmit from '@/hooks/useApiSubmit';
import candidateProfile from '@/services/candidateProfile';
import candidatesServices, {
  CandidateProfileResponse,
  CandidateResponse,
  PartialCandidateToSubmit,
} from '@/services/candidates';
import { FetchApiIds } from '@/types';
import { PersonType } from '@/types/api';
import StaffMemberProfessionalBackgroundComponent from './StaffMemberProfessionalBackground.component';
import { useStaffMemberInfoContext } from '../StaffMemberInfo/StaffMemberInfo.context';

const StaffMemberProfessionalBackground = (): JSX.Element => {
  const { id = '' } = useParams();
  const toast = useToastContext();
  const { staffMember, refreshUser } = useStaffMemberInfoContext();

  const candidateProfileResponse = useApiFetch<CandidateProfileResponse>(
    [FetchApiIds.CANDIDATE_PROFILE, staffMember?.person.id || ''],
    async () => candidatesServices.getProfileByID(staffMember?.person.id || ''),
    { enabled: Boolean(staffMember?.person.id) }
  );

  const updateCandidateMutation = useApiSubmit<CandidateResponse, PartialCandidateToSubmit>(
    candidatesServices.updateCandidateIndividuallyById
  );

  const getCandidateProjects = useApiFetch([FetchApiIds.CANDIDATE_PROJECTS, id], async () =>
    candidateProfile.getAllProjects({ candidateId: id })
  );

  const handleSubmit = async ({
    githubUrl,
  }: Partial<Pick<PersonType, 'githubUrl' | 'trioProfileUrl'>>): Promise<void> => {
    try {
      toast.setMessage('Saving information...');
      toast.setSeverity('info');
      toast.showToast();

      await updateCandidateMutation.mutateAsync({
        candidate: {
          githubUrl,
          id: staffMember?.person.id,
        },
      });

      toast.setMessage('The staff member information has been updated.');
      toast.setSeverity('success');
      toast.showToast();
      refreshUser?.();
    } catch {
      toast.setMessage('There was an error saving the data.');
      toast.setSeverity('error');
      toast.showToast();
    }
  };

  return (
    <StaffMemberProfessionalBackgroundComponent
      staffMember={staffMember}
      onSubmit={handleSubmit}
      candidateProfile={candidateProfileResponse.data?.profile}
      projects={getCandidateProjects.data?.projects}
    />
  );
};

export default StaffMemberProfessionalBackground;
